/**
 * `/anmelden` -- the one view an unauthenticated stranger can reach. Boot.tsx
 * renders it in place of the shell whenever `GET /admin-api/me` answers 401
 * (lib/bootState.ts's `'login'` screen); it never renders any navigation, no
 * tenant name and no environment chip, because it runs before `me` exists and
 * has nothing truthful to say about a tenant.
 *
 * ITS WHOLE JOB IS TO BE BORING AND NON-INFORMATIVE ON FAILURE. The three
 * rules that make it that way live in lib/loginFlow.ts (the decisions) and in
 * `submitRequestCode`/`submitVerifyCode` below (APPLYING those decisions):
 *
 *  1. `outcomeForRequestCode` renders /auth/request's message verbatim and
 *     always advances -- the server already merged sent / unknown_email /
 *     rate_limited / send_failed into one body, and re-deciding anything
 *     about it here would undo that.
 *  2. `advance: false` happens for exactly one reason, EMAIL_UNDELIVERABLE:
 *     there is no code coming, so the code step never shows.
 *  3. `isInvalidCodeError` is the only thing this view branches on for a
 *     verify failure. Every other reason the server could have rejected the
 *     code (wrong, expired, too many attempts, no such request) renders the
 *     exact same `t('login.invalidCode')`.
 *
 * TASK 7 REVIEW I-1: pinning the decisions in lib/loginFlow.ts is not enough
 * -- a mutation planted at a CALL SITE (bypass `outcome.advance`; split
 * `invalid_code` into two messages) escaped the whole gate, because nothing
 * exercised the code that applies the decision, only the decision itself.
 * `submitRequestCode` and `submitVerifyCode` are that application, extracted
 * the same way Boot.tsx's `performLogout(queryClient, redirect, logout)`
 * is: async functions that take their collaborators (the API call, the
 * state setters) as parameters, so test/login.test.ts can exercise the FULL
 * submit -> decide -> render-the-answer path in Node, with no renderer. The
 * `Login` component below calls them with its real setters and owns no
 * decision or message-selection logic of its own -- only plumbing (pending,
 * the Turnstile token/ref, which step is mounted).
 *
 * DESIGN SPIKE: a centred card on the app background, with a two-step
 * indicator. Every rule above, every message and every disabled condition is
 * byte-for-byte what it was; only the frame around them is new.
 */
import { useCallback, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { AUTH_API } from '../lib/api';
import { guardedAction } from '../lib/gate';
import { t } from '../lib/i18n';
import { isInvalidCodeError, outcomeForRequestCode, type RequestCodeResponse } from '../lib/loginFlow';
import { cn } from '../lib/utils';
import { errorMessage } from '../components/ErrorNotice';
import { Turnstile, type TurnstileHandle } from '../components/Turnstile';
import { Button } from '../components/ui/button';

export interface LoginProps {
  siteKey: string;
  onSignedIn: () => void;
}

export type Step = 'email' | 'code';
export type MessageKind = 'info' | 'error';

export interface RequestCodeIO {
  requestCode: (json: { email: string; turnstileToken: string }) => Promise<RequestCodeResponse>;
  setMessage: (message: string) => void;
  setMessageKind: (kind: MessageKind) => void;
  setStep: (step: Step) => void;
}

/**
 * Rules 1 & 2, APPLIED. The only branch here is `outcome.advance` itself --
 * computed once, by `outcomeForRequestCode`, and never re-derived or
 * second-guessed at this call site.
 */
export async function submitRequestCode(email: string, token: string, io: RequestCodeIO): Promise<void> {
  try {
    const response = await io.requestCode({ email, turnstileToken: token });
    const outcome = outcomeForRequestCode(response);
    io.setMessage(outcome.message);
    io.setMessageKind(outcome.advance ? 'info' : 'error');
    if (outcome.advance) io.setStep('code');
  } catch (error) {
    // Not one of the three rules: a transport/validation failure on the
    // request itself (turnstile_required, network, ...), not an
    // anti-enumeration case. The shell's ordinary generic-error copy.
    io.setMessage(errorMessage(error));
    io.setMessageKind('error');
  }
}

export interface VerifyCodeIO {
  verifyCode: (json: { email: string; code: string }) => Promise<unknown>;
  onSignedIn: () => void;
  setMessage: (message: string) => void;
  setMessageKind: (kind: MessageKind) => void;
}

/**
 * Rule 3, APPLIED. `isInvalidCodeError` is the only thing this function
 * branches on; every reason the server could reject a code collapses into
 * the exact same `t('login.invalidCode')` call -- never the server's own
 * `message`, which could one day carry a reason.
 *
 * Returns whether the sign-in succeeded, so the component can decide whether
 * to re-enable the form -- that is plumbing, not a rule this function owns.
 */
export async function submitVerifyCode(email: string, code: string, io: VerifyCodeIO): Promise<boolean> {
  try {
    await io.verifyCode({ email, code });
    // The session cookie arrived via the relayed Set-Cookie; onSignedIn()
    // is what makes Boot re-ask GET /admin-api/me and notice it.
    io.onSignedIn();
    return true;
  } catch (error) {
    io.setMessage(isInvalidCodeError(error) ? t('login.invalidCode') : errorMessage(error));
    io.setMessageKind('error');
    return false;
  }
}

/** The two-step indicator. Presentational: `step` is the component's own state,
 *  not a decision this function makes. */
function StepDots({ step }: { step: Step }) {
  return (
    <span aria-hidden="true" className="flex items-center gap-1.5">
      <span className={cn('h-1 w-6 rounded-full transition-colors', 'bg-accent')} />
      <span className={cn('h-1 w-6 rounded-full transition-colors', step === 'code' ? 'bg-accent' : 'bg-line')} />
    </span>
  );
}

export function Login({ siteKey, onSignedIn }: LoginProps) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [turnstileUnavailable, setTurnstileUnavailable] = useState(false);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageKind, setMessageKind] = useState<MessageKind>('info');
  const turnstileRef = useRef<TurnstileHandle | null>(null);

  /**
   * BOTH STEPS ARE TIER 1 under lib/gate.ts's ruling, and this view is
   * pre-session so it has no `useMutationShell` to hang the gate on: one ref
   * for the form, since only one of the two steps is ever mounted at a time.
   *
   * Requesting a code SENDS AN EMAIL and burns a single-use Turnstile token;
   * verifying one consumes a single-use code. Both were guarded by `pending`,
   * which is React state and therefore still false when the second half of a
   * double-submit arrives -- the same defect the final review found at the
   * eighteen sites inside the shell, one screen earlier.
   */
  const formGate = useRef<string | null>(null);

  const handleRequestCode = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (turnstileUnavailable || !token) return;
      guardedAction(formGate, 'requestCode', () => {
        setPending(true);
        return submitRequestCode(email, token, {
          requestCode: AUTH_API.requestCode,
          setMessage,
          setMessageKind,
          setStep,
        }).finally(() => {
          // Every submit, success or failure: a Turnstile token is single-use,
          // and a stale one left in place would fail every retry with no
          // visible reason (Turnstile.tsx's docblock).
          turnstileRef.current?.reset();
          setToken(null);
          setPending(false);
        });
      });
    },
    [email, token, turnstileUnavailable],
  );

  const handleVerifyCode = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      guardedAction(formGate, 'verifyCode', () => {
        setPending(true);
        return submitVerifyCode(email, code, {
          verifyCode: AUTH_API.verifyCode,
          onSignedIn,
          setMessage,
          setMessageKind,
        }).then((signedIn) => {
          // `pending` is deliberately left true on success: this view is about
          // to unmount once Boot notices the new session.
          if (!signedIn) setPending(false);
        });
      });
    },
    [email, code, onSignedIn],
  );

  const handleOtherEmail = useCallback(() => {
    setStep('email');
    setCode('');
    setMessage(null);
    // No explicit widget reset call: the Turnstile widget only mounts during
    // the email step (below), so returning to it mounts a brand new widget --
    // already the reset the brief asks for, without a stale ref to call.
  }, []);

  return (
    <section className="login-view grid min-h-screen place-items-center bg-bg px-4 py-10">
      <div className="w-full max-w-[24rem]">
        <div className="rounded-card border border-line bg-surface p-6 shadow-float">
          <div className="mb-5 flex items-center justify-between gap-3">
            <h1 className="text-[1.0625rem]">{t('login.title')}</h1>
            <StepDots step={step} />
          </div>

          {step === 'email' ? (
            <form className="login-form grid gap-4" onSubmit={handleRequestCode}>
              <label>
                {t('login.emailLabel')}
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  disabled={pending}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </label>
              <Turnstile
                ref={turnstileRef}
                siteKey={siteKey}
                onToken={setToken}
                onUnavailable={() => setTurnstileUnavailable(true)}
              />
              {turnstileUnavailable ? (
                <p className="login-turnstile-unavailable">{t('login.turnstileUnavailable')}</p>
              ) : null}
              <Button
                type="submit"
                variant="default"
                className="w-full"
                disabled={pending || turnstileUnavailable || !token}
              >
                {t('login.submitEmail')}
              </Button>
            </form>
          ) : (
            <form className="login-form grid gap-4" onSubmit={handleVerifyCode}>
              <p className="login-code-hint text-[0.8125rem] text-muted">{t('login.codeHint')}</p>
              <label>
                {t('login.codeLabel')}
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  value={code}
                  disabled={pending}
                  className="text-center font-mono text-lg tracking-[0.4em]"
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </label>
              <Button type="submit" variant="default" className="w-full" disabled={pending}>
                {t('login.submitCode')}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={handleOtherEmail}>
                {t('login.otherEmail')}
              </Button>
            </form>
          )}

          <p
            className={cn(
              'login-message mt-4 min-h-[1.25rem] text-[0.8125rem]',
              messageKind === 'error' ? 'text-danger' : 'text-muted',
            )}
            aria-live="polite"
            data-kind={messageKind}
          >
            {message}
          </p>
        </div>
      </div>
    </section>
  );
}
